import { z } from 'zod';
import type { StoreApi } from 'zustand';

import type { Make } from 'Types/make';
import type { Model } from 'Types/model';

import type { InsightsStore, InsightStoreInitialValues } from './InsightsStore.types';

const makeModelParamsSchema = z.object({
	make: z.string(),
	model: z.string(),
});

type ResolveMakeModelParamsFromMakeModelStoresParams = {
	make?: string | string[];
	makes: Make[];
	model?: string | string[];
	models: Model[];
};

export const resolveMakeModelFromMakeModelStores = ({
	make: makeParam,
	makes,
	model: modelParam,
	models,
}: ResolveMakeModelParamsFromMakeModelStoresParams): InsightStoreInitialValues => {
	const buildMakeModelParams = makeModelParamsSchema.safeParse({ make: makeParam, model: modelParam });

	if (!buildMakeModelParams.success) {
		throw new Error('Unable to resolve make and model from query params');
	}

	const { make, model } = buildMakeModelParams.data;

	const selectedMake = makes.find((m) => m.slug.toLowerCase() === make.toLowerCase());

	if (!selectedMake) {
		throw new Error(`Unable to resolve make from makes store - ${make}`);
	}

	const selectedModel = models.find(
		(m) => m.makeId === selectedMake.id && m.name.toLowerCase() === model.toLowerCase(),
	);

	if (!selectedModel) {
		throw new Error(`Unable to resolve model from models store - ${model} for make ${make}`);
	}

	return {
		make: selectedMake,
		model: selectedModel,
	};
};

export const setMakeAndModel =
	(set: StoreApi<InsightsStore>['setState']) =>
	(make: string, model: string, makes: Make[], models: Model[]): void =>
		set((state) => {
			try {
				const { make: resolvedMake, model: resolvedModel } = resolveMakeModelFromMakeModelStores({
					make,
					makes,
					model,
					models,
				});

				return {
					...state,
					make: resolvedMake,
					model: resolvedModel,
				};
			} catch (error) {
				return state;
			}
		});
